/**
 * Centralised route table.
 *
 * Each entry: `{path, element: <Component />, roles?: ["super_admin", ...]}`.
 * `roles=undefined` → any authenticated user.
 * App.js wraps each entry in `<ProtectedRoute roles={roles}>` automatically.
 */
import React from "react";
import RoleHome from "@/pages/RoleHome";
import Students from "@/pages/Students";
import Admissions from "@/pages/Admissions";
import Fees from "@/pages/Fees";
import Marksheets from "@/pages/Marksheets";
import Certificates from "@/pages/Certificates";
import Progress from "@/pages/Progress";
import Donations from "@/pages/Donations";
import { HostelsPage, SchoolsPage, UsersPage, AuditPage } from "@/pages/AdminPages";
import StudentEdit from "@/pages/StudentEdit";
import Approvals from "@/pages/Approvals";
import DonorFeed from "@/pages/DonorFeed";
import Alumni from "@/pages/Alumni";
import NominateDonor from "@/pages/NominateDonor";
import TeacherMarks from "@/pages/TeacherMarks";
import TwoFactorSetup from "@/pages/TwoFactorSetup";
import TrustsAdmin from "@/pages/TrustsAdmin";
import NotificationsAdmin from "@/pages/NotificationsAdmin";
import Reports from "@/pages/Reports";
import Expenditure from "@/pages/Expenditure";
import AdminDonations from "@/pages/AdminDonations";
import RbacAdmin from "@/pages/RbacAdmin";
import TenantOnboard from "@/pages/TenantOnboard";
import MasterData from "@/pages/MasterData";
import Messages from "@/pages/Messages";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Settings from "@/pages/Settings";
import SacHome from "@/pages/SacHome";
import SacIncomplete from "@/pages/SacIncomplete";
import Subscriptions from "@/pages/Subscriptions";
import TrustSubscriptionsAdmin from "@/pages/TrustSubscriptionsAdmin";
import TenantAudit from "@/pages/TenantAudit";
import DonorPortfolio from "@/pages/DonorPortfolio";
import Backup from "@/pages/Backup";
import GenderReport from "@/pages/GenderReport";
import Attendance from "@/pages/Attendance";
import Transport from "@/pages/Transport";
import Library from "@/pages/Library";
import WhatsAppSettings from "@/pages/WhatsAppSettings";
import MessMenu from "@/pages/MessMenu";
import FeeEscalation from "@/pages/FeeEscalation";
import Health from "@/pages/Health";
import MessAttendance from "@/pages/MessAttendance";
import MessScan from "@/pages/MessScan";

const SUPER = ["super_admin"];
const SUPER_OR_TRUST = ["super_admin", "trust_admin"];
const SUPER_HOSTEL_SCHOOL = ["super_admin", "hostel_admin", "school_admin"];
const SUPER_HOSTEL_SCHOOL_TRUST = ["super_admin", "hostel_admin", "school_admin", "trust_admin"];
const SUPER_HOSTEL_SCHOOL_DONOR_TEACHER = ["super_admin", "hostel_admin", "school_admin", "donor", "teacher"];
const SUPER_SCHOOL_TEACHER = ["super_admin", "school_admin", "teacher"];

export const APP_ROUTES = [
  { path: "/app", element: <RoleHome /> },
  { path: "/app/payment-success", element: <PaymentSuccess />, roles: ["parent"] },
  { path: "/app/students", element: <Students />, roles: ["super_admin", "hostel_admin", "school_admin", "teacher", "trust_admin", "parent", "donor"] },
  { path: "/app/students/:studentId", element: <StudentEdit />, roles: SUPER_HOSTEL_SCHOOL },
  { path: "/app/approvals", element: <Approvals />, roles: SUPER_HOSTEL_SCHOOL },
  { path: "/app/feed", element: <DonorFeed />, roles: ["donor"] },
  { path: "/app/alumni", element: <Alumni />, roles: SUPER_HOSTEL_SCHOOL_DONOR_TEACHER },
  { path: "/app/nominate-donor", element: <NominateDonor />, roles: SUPER_HOSTEL_SCHOOL },
  { path: "/app/teacher/marks", element: <TeacherMarks />, roles: ["teacher"] },
  { path: "/app/admissions", element: <Admissions />, roles: SUPER_HOSTEL_SCHOOL },
  { path: "/app/fees", element: <Fees />, roles: SUPER_HOSTEL_SCHOOL },
  { path: "/app/marksheets", element: <Marksheets />, roles: SUPER_SCHOOL_TEACHER },
  { path: "/app/certificates", element: <Certificates />, roles: SUPER_HOSTEL_SCHOOL },
  { path: "/app/progress", element: <Progress />, roles: SUPER_HOSTEL_SCHOOL },
  { path: "/app/donations", element: <Donations />, roles: ["donor", "super_admin"] },
  { path: "/app/hostels", element: <HostelsPage />, roles: SUPER_OR_TRUST },
  { path: "/app/schools", element: <SchoolsPage />, roles: SUPER_OR_TRUST },
  { path: "/app/users", element: <UsersPage />, roles: SUPER },
  { path: "/app/audit", element: <AuditPage />, roles: SUPER_OR_TRUST },
  { path: "/app/trusts", element: <TrustsAdmin />, roles: SUPER },
  { path: "/app/notifications", element: <NotificationsAdmin />, roles: SUPER },
  { path: "/app/settings", element: <Settings />, roles: SUPER },
  { path: "/app/sac", element: <SacHome />, roles: ["super_admin", "sac_admin"] },
  { path: "/app/sac/incomplete", element: <SacIncomplete />, roles: ["super_admin", "sac_admin"] },
  { path: "/app/subscriptions", element: <TrustSubscriptionsAdmin />, roles: SUPER },
  { path: "/app/super/audit", element: <TenantAudit />, roles: SUPER },
  { path: "/app/org-subscriptions", element: <Subscriptions />, roles: SUPER },
  { path: "/app/backup", element: <Backup />, roles: SUPER },
  { path: "/app/donor-portfolio", element: <DonorPortfolio />, roles: ["donor"] },
  { path: "/app/security", element: <TwoFactorSetup /> },
  { path: "/app/reports", element: <Reports />, roles: SUPER_HOSTEL_SCHOOL_TRUST },
  { path: "/app/reports/gender", element: <GenderReport />, roles: ["super_admin", "hostel_admin", "trust_admin"] },
  { path: "/app/expenditure", element: <Expenditure />, roles: SUPER_HOSTEL_SCHOOL_TRUST },
  { path: "/app/admin-donations", element: <AdminDonations />, roles: SUPER_HOSTEL_SCHOOL_TRUST },
  { path: "/app/rbac", element: <RbacAdmin />, roles: SUPER },
  { path: "/app/onboard", element: <TenantOnboard />, roles: SUPER },
  { path: "/app/master-data", element: <MasterData />, roles: SUPER },
  { path: "/app/messages", element: <Messages /> },
  { path: "/app/attendance", element: <Attendance />, roles: ["super_admin", "hostel_admin", "school_admin", "teacher", "trust_admin"] },
  { path: "/app/transport", element: <Transport />, roles: SUPER_HOSTEL_SCHOOL_TRUST },
  { path: "/app/library", element: <Library />, roles: ["super_admin", "hostel_admin", "school_admin", "teacher", "trust_admin"] },
  { path: "/app/whatsapp", element: <WhatsAppSettings />, roles: SUPER },
  { path: "/app/mess-menu", element: <MessMenu />, roles: ["super_admin", "hostel_admin"] },
  { path: "/app/fee-escalation", element: <FeeEscalation />, roles: SUPER },
  { path: "/app/health", element: <Health />, roles: ["super_admin", "trust_admin", "hostel_admin", "school_admin", "teacher"] },
  { path: "/app/mess-attendance", element: <MessAttendance />, roles: ["super_admin", "trust_admin", "hostel_admin", "teacher"] },
  { path: "/app/mess-scan", element: <MessScan />, roles: ["super_admin", "hostel_admin", "teacher"] },
];
